// ==================== RADIO BUTTON COMPONENT ====================

import { forwardRef, useId } from 'react';
import { cn } from '../../utils/helpers';

interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  description?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
}

const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      description,
      error,
      size = 'md',
      className,
      disabled,
      checked,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const radioId = id || generatedId;

    const sizes = {
      sm: {
        outer: 'w-4 h-4',
        inner: 'w-2 h-2',
        label: 'text-sm',
      },
      md: {
        outer: 'w-5 h-5',
        inner: 'w-2.5 h-2.5',
        label: 'text-sm',
      },
      lg: {
        outer: 'w-6 h-6',
        inner: 'w-3 h-3',
        label: 'text-base',
      },
    };

    return (
      <div className={cn('relative', className)}>
        <label
          htmlFor={radioId}
          className={cn(
            'inline-flex items-start gap-3',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
        >
          {/* Hidden Radio */}
          <input
            ref={ref}
            type="radio"
            id={radioId}
            disabled={disabled}
            checked={checked}
            className="sr-only peer"
            {...props}
          />

          {/* Custom Radio */}
          <div
            className={cn(
              'relative shrink-0 rounded-full border-2 transition-all duration-200 flex items-center justify-center',
              sizes[size].outer,
              checked
                ? 'border-(--color-signal-orange)'
                : 'border-(--color-border-gray) hover:border-(--color-signal-orange)/50',
              disabled && 'opacity-50',
              error && !checked && 'border-(--color-error)'
            )}
          >
            {/* Inner Circle */}
            <div
              className={cn(
                'rounded-full bg-(--color-signal-orange) transition-all duration-200',
                sizes[size].inner,
                checked ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
              )}
            />
          </div>

          {/* Label & Description */}
          {(label || description) && (
            <div className="flex flex-col pt-0.5">
              {label && (
                <span
                  className={cn(
                    'font-medium',
                    sizes[size].label,
                    disabled ? 'text-(--color-muted-gray)' : 'text-(--color-soft-gray)'
                  )}
                >
                  {label}
                </span>
              )}
              {description && (
                <span className="text-xs text-(--color-muted-gray) mt-0.5">
                  {description}
                </span>
              )}
            </div>
          )}
        </label>

        {/* Error */}
        {error && (
          <p className="mt-1 text-sm text-(--color-error) ml-8">{error}</p>
        )}
      </div>
    );
  }
);

Radio.displayName = 'Radio';

// ==================== RADIO GROUP COMPONENT ====================

interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface RadioGroupProps {
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  name: string;
  label?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  direction?: 'horizontal' | 'vertical';
  className?: string;
  required?: boolean;
}

const RadioGroup = ({
  options,
  value,
  onChange,
  name,
  label,
  error,
  size = 'md',
  direction = 'vertical',
  className,
  required,
}: RadioGroupProps) => {
  return (
    <div className={className}>
      {/* Group Label */}
      {label && (
        <label className="block text-sm font-medium text-(--color-soft-gray) mb-3">
          {label}
          {required && <span className="text-(--color-error) ml-1">*</span>}
        </label>
      )}

      {/* Radio Options */}
      <div
        className={cn(
          'flex gap-4',
          direction === 'vertical' ? 'flex-col' : 'flex-row flex-wrap'
        )}
        role="radiogroup"
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange?.(option.value)}
            label={option.label}
            description={option.description}
            disabled={option.disabled}
            size={size}
          />
        ))}
      </div>

      {/* Error */}
      {error && (
        <p className="mt-2 text-sm text-(--color-error)">{error}</p>
      )}
    </div>
  );
};

export { Radio, RadioGroup };

